import { atom } from 'recoil';
import type { RecoilState } from 'recoil';
import type { StatusAuditLogs } from 'hooks/useWorklistItemAuditLogs';
import { broadcastChannelSynchronizerEffect } from 'utils/recoilEffects';
import type { WorklistSurfaceType } from './types';
import type {
  FilterStateType,
  FilterValueType,
  WorklistFiltersType,
} from '../WorklistFilters/types';
import { CustomWorklistDetail, GetWorklistQueryVariables } from 'generated/graphql';
import type {
  CustomWorklistAdvancedSettings,
  CustomWorklistInternalSettings,
} from 'generated/graphql';
export type ClaimCaseDialogState = {
  open: boolean;
  caseId: string;
  fullName: string;
  action: 'QUEUE' | 'READ' | '';
  callback?: () => void;
  isAddendum?: boolean;
};

export const claimCaseDialogStateDefault = {
  isAddendum: false,
  open: false,
  caseId: '',
  fullName: '',
  action: '',
  callback: () => {},
} as const;

export const claimCaseDialogState: RecoilState<ClaimCaseDialogState> = atom<ClaimCaseDialogState>({
  key: 'worklist.claimCaseDialogState',
  default: claimCaseDialogStateDefault,
});

export type CaseAuditLogModalState = {
  open: boolean;
  auditLogs: StatusAuditLogs;
};

export const caseAuditLogModalStateDefault: CaseAuditLogModalState = {
  open: false,
  auditLogs: {
    statusChanges: [],
    customStatusChanges: [],
    criticalResultStatusChanges: [],
  },
};

export const caseAuditLogModalState: RecoilState<CaseAuditLogModalState> = atom({
  key: 'worklist.caseAuditLogModalState',
  default: caseAuditLogModalStateDefault,
});

export type WorklistAutoLoadState = {
  surface: Nullable<WorklistSurfaceType>;
  // Sort URL params from the worklist surface. The Reporter reads this to
  // initialize its WorklistFiltersContext so the autoload toggle and
  // "viewing from X" cues match the worklist's saved sort.
  searchParamsString: Nullable<string>;
  // Computed worklist query variables shared between the worklist table and
  // the autoloadNextCase mutation. The mutation picks the autoload-relevant
  // subset (filters, sort, search) — pagination fields are ignored server-side.
  variables: Nullable<GetWorklistQueryVariables>;
};

export const worklistAutoLoadStateDefault: WorklistAutoLoadState = {
  surface: null,
  searchParamsString: null,
  variables: null,
};

export const worklistAutoLoadState: RecoilState<WorklistAutoLoadState> = atom({
  key: 'worklistAutoLoadState',
  default: worklistAutoLoadStateDefault,
  effects: [broadcastChannelSynchronizerEffect()],
});

export const patientJacketShowRelevantPriorsOnlyState: RecoilState<boolean> = atom({
  key: 'patientJacketShowRelevantPriorsOnlyState',
  default: false,
  effects: [broadcastChannelSynchronizerEffect()],
});

export const assignExamDialogState: RecoilState<boolean> = atom({
  key: 'assignExamDialogIsOpen',
  default: false,
});

// Separate atom for the jacket dropdown's assign flow, so it doesn't
// conflict with the worklist table's own AssignExamDialog instance.
export const jacketAssignExamDialogState: RecoilState<boolean> = atom({
  key: 'jacketAssignExamDialogIsOpen',
  default: false,
});

export const CUSTOM_WORKLIST_DIALOG_MODE = {
  CREATE: 'create',
  UPDATE: 'update',
  CLONE: 'clone',
} as const;

export type CustomWorklistDialogMode =
  (typeof CUSTOM_WORKLIST_DIALOG_MODE)[keyof typeof CUSTOM_WORKLIST_DIALOG_MODE];

export const defaultAdvancedSettings: CustomWorklistAdvancedSettings = {
  statsToTop: true,
  showExamsCount: false,
  lockFilters: false,
  worklistPreferences: {
    columns: [],
    sorting: [],
    filters: [],
  },
};

export type CustomWorklistDialogState = {
  isOpen: boolean;
  mode: CustomWorklistDialogMode;
  worklist: {
    smid: string;
    name: string;
    internalSettings: CustomWorklistInternalSettings | null;
  } | null;
  advancedSettings: CustomWorklistAdvancedSettings;
  initialFilters?: Partial<Record<WorklistFiltersType, FilterValueType>>;
  onSuccess?: (customWorklist: CustomWorklistDetail) => void;
};

export const customWorklistDialogState = atom<CustomWorklistDialogState>({
  key: 'customWorklistDialogState',
  default: {
    isOpen: false,
    mode: CUSTOM_WORKLIST_DIALOG_MODE.CREATE,
    worklist: null,
    advancedSettings: defaultAdvancedSettings,
    initialFilters: {},
    onSuccess: (customWorklist: CustomWorklistDetail) => {},
  },
});

export const activeCustomWorklistIDsState = atom<string[]>({
  key: 'activeCustomWorklistIDsState',
  default: [],
  effects: [broadcastChannelSynchronizerEffect()],
});

export const activeCustomWorklistFiltersState: RecoilState<FilterStateType | null> = atom({
  key: 'activeCustomWorklistFiltersState',
  default: null as FilterStateType | null,
  effects: [broadcastChannelSynchronizerEffect()],
});
